import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { MasterTransaction } from '../types';
import { DashboardDateRange, DashboardTipType } from '../contexts/DashboardFiltersContext';
import { isPos, isCash, isJar, isDispute, isAllocated, isAllocatedOrPendingPOS, filterTransactionsByLocation } from '../lib/transactionUtils';
import { getSettings } from './settingsService';
import { getStaff } from './staffService';

export interface AnalyticsData {
  weeklyTotal: number;
  avgDaily: number;
  peakDay: string;
  chartData: { name: string; Morning: number; Evening: number }[];
  sourceData: { name: string; value: number }[];
  employeeData: { name: string; value: number }[];
}

const getLocalDateString = (date: Date) => {
    return date.getFullYear() + '-' + 
           String(date.getMonth() + 1).padStart(2, '0') + '-' + 
           String(date.getDate()).padStart(2, '0');
};

export const getAnalyticsData = async (
  tenantId: string, 
  dateRange: DashboardDateRange, 
  tipType: DashboardTipType,
  locationId?: string
): Promise<AnalyticsData> => {
  const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
  
  // Get timezone from settings to match DashboardSummary
  const settings = await getSettings(tenantId);
  const timeZone = settings?.timezone || 'UTC';

  const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
  });
  const [year, month, day] = formatter.format(new Date()).split('-').map(Number);
  
  const dEnd = new Date(year, month - 1, day);
  const dStart = new Date(year, month - 1, day);

  let start = '';
  let end = '';

  if (dateRange === 'last_closed_pay_period') {
    // Try to get actual last closed pay period from payroll_exports
    const exportsRef = collection(db, 'tenants', tenantId, 'payroll_exports');
    const q = query(exportsRef, where('tenantId', '==', tenantId), where('status', '==', 'Approved'));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const sorted = snapshot.docs.map(doc => doc.data()).sort((a, b) => b.endDate.localeCompare(a.endDate));
      start = sorted[0].startDate;
      end = sorted[0].endDate;
    } else {
      dStart.setDate(dStart.getDate() - 14);
      start = getLocalDateString(dStart);
      end = getLocalDateString(dEnd);
    }
  } else if (dateRange === 'current_pay_period') {
    // Try to get actual open pay period from payroll_periods
    const periodsRef = collection(db, 'tenants', tenantId, 'payroll_periods');
    const q = query(periodsRef, where('status', '==', 'Open'));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const sorted = snapshot.docs.map(doc => doc.data()).sort((a, b) => b.startDate.localeCompare(a.startDate));
      start = sorted[0].startDate;
      end = sorted[0].endDate;
    } else {
      dStart.setDate(dStart.getDate() - 7);
      start = getLocalDateString(dStart);
      end = getLocalDateString(dEnd);
    }
  } else {
    switch(dateRange) {
        case 'today': break;
        case 'last_30_days': dStart.setDate(dStart.getDate() - 30); break;
        case 'month_to_date': dStart.setDate(1); break;
        default: dStart.setDate(dStart.getDate() - 30);
    }
    start = getLocalDateString(dStart);
    end = getLocalDateString(dEnd);
  }

  // Fetch all transactions to ensure parent lookups always succeed
  const snapshot = await getDocs(masterRef);
  const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTransaction));
  
  const rangeTips = allDocs.filter(t => t.tipDate >= start && t.tipDate <= end);
  const locationFilteredTips = filterTransactionsByLocation(rangeTips, locationId || 'all');
  
  const finalizedTips = locationFilteredTips.filter(t => {
      if (!isAllocatedOrPendingPOS(t, allDocs)) return false;
      if (tipType === 'total') return true;
      if (tipType === 'pos') return isPos(t, allDocs);
      if (tipType === 'cash') return isCash(t, allDocs);
      if (tipType === 'jar') return isJar(t, allDocs);
      if (tipType === 'dispute') return isDispute(t, allDocs);
      return true;
  });

  const weeklyTotal = finalizedTips.reduce((acc, t) => acc + t.amount, 0);
  
  // Tip Source Breakdown with separate trackers
  const sources = {
    POS: 0,
    Cash: 0,
    Jar: 0,
    Dispute: 0
  };

  // Employee Aggregation
  const employeeTips: Record<string, number> = {};

  // Group by date
  const byDate: Record<string, { Morning: number; Evening: number }> = {};
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Initialize range
  const startDate = new Date(start + 'T00:00:00');
  const endDate = new Date(end + 'T00:00:00');
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dateStr = getLocalDateString(d);
    byDate[dateStr] = { Morning: 0, Evening: 0 };
  }

  // Fetch staff list for reliable posId resolution
  const staffList = await getStaff(tenantId);
  const staffIdToPosMap: Record<string, string> = {};
  const staffNameToPosMap: Record<string, string> = {};
  staffList.forEach(s => {
    if (s.id && s.posId) staffIdToPosMap[s.id] = s.posId;
    if (s.name && s.posId) staffNameToPosMap[s.name.toLowerCase()] = s.posId;
  });

  finalizedTips.forEach(tip => {
    const amount = tip.amount || 0;

    // Track sources cleanly based on true classifications
    if (isPos(tip, allDocs)) {
      sources.POS += amount;
    } else if (isJar(tip, allDocs)) {
      sources.Jar += amount;
    } else if (isDispute(tip, allDocs)) {
      sources.Dispute += amount;
    } else {
      sources.Cash += amount;
    }

    // Track employee performance with explicit identifier
    const resolvedPosId = (tip.staffId && staffIdToPosMap[tip.staffId]) ||
                          (tip.staffName && staffNameToPosMap[tip.staffName.toLowerCase()]) ||
                          tip.posId || '';

    const staffIdTag = resolvedPosId ? `[POS: ${resolvedPosId}]` : (tip.staffId ? `[ID: ${tip.staffId.slice(0, 6)}]` : '');
    const employeeName = tip.staffName ? `${tip.staffName} ${staffIdTag}`.trim() : 'Unknown Staff';
    employeeTips[employeeName] = (employeeTips[employeeName] || 0) + amount;

    if (byDate[tip.tipDate]) {
      // DEBUG: Log the values for debugging shift issues
      if (Math.random() < 0.1) { // Log occasionally to avoid spamming
        console.log('DEBUG: Tip processing', { 
            id: tip.id, 
            shift: tip.shift,
            shiftName: tip.shiftName, 
            auditContext: tip.auditContext,
            timestamp: tip.timestamp // Added timestamp
        });
      }
      
      // Check shift, shiftName, and auditContext to support different schemas and legacy compatibility.
      const shiftString = (
        tip.shift || 
        tip.shiftName || 
        tip.auditContext || 
        ''
      ).toLowerCase();
      
      const isMorning = shiftString.includes('morning') || 
                        shiftString.includes('lunch') || 
                        shiftString.includes('am') || 
                        shiftString.includes('day') || 
                        shiftString.includes('breakfast');
      
      if (isMorning) {
        byDate[tip.tipDate].Morning += amount;
      } else {
        byDate[tip.tipDate].Evening += amount;
      }
    }
  });

  const chartData = Object.entries(byDate).map(([date, shifts]) => {
    const d = new Date(date + 'T00:00:00');
    return {
      name: days[d.getDay()],
      ...shifts
    };
  });

  const sourceData = [
    { name: 'POS Tips', value: sources.POS },
    { name: 'Cash Tips', value: sources.Cash },
    { name: 'Jar Tips', value: sources.Jar },
    { name: 'Disputes', value: sources.Dispute }
  ].filter(s => s.value > 0);
  
  // Sort and group employees
  const sortedEmployees = Object.entries(employeeTips)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const top5 = sortedEmployees.slice(0, 5);
  const others = sortedEmployees.slice(5);
  
  const employeeData = [...top5];
  if (others.length > 0) {
    employeeData.push({
      name: 'Others',
      value: others.reduce((acc, curr) => acc + curr.value, 0)
    });
  }

  const avgDaily = Object.keys(byDate).length > 0 ? weeklyTotal / Object.keys(byDate).length : 0;
  
  let peakDay = 'N/A';
  let maxAmount = 0;
  chartData.forEach(d => {
    const total = d.Morning + d.Evening;
    if (total > maxAmount) {
      maxAmount = total;
      peakDay = d.name;
    }
  });

  return {
    weeklyTotal,
    avgDaily,
    peakDay,
    chartData,
    sourceData,
    employeeData
  };
};
